/**
 * FO-415 (batch-57 overeenkomst-vervolg) — vervolgacties na het genereren van
 * een overeenkomst + de automatische bruikleen-trigger bij middel-wisselingen.
 *
 * Twee samenhangende delen, één bestand omdat ze dezelfde statusketen delen:
 *
 *  A. **Vervolgblok "Volgende stappen"** (spec §3.A) voor ÁLLE handmatig
 *     gegenereerde overeenkomsten - bruikleen, arbeids-, studie- en
 *     autoregeling-overeenkomsten. Koppelen (medewerker + eventueel middel),
 *     delen (FO-385-vrijgave, downloaden, mailen) en ondertekenen langs drie
 *     routes naast elkaar: in-app tekenen (FO-384), downloaden voor een natte
 *     handtekening, of de getekende PDF uploaden als versie n+1 van HETZELFDE
 *     document (FO-378, aanname A1).
 *
 *  B. **Bruikleen-trigger** (spec §3.B): één uitgifte-moment levert één taak met
 *     volledig voorgevulde wizard; inname beëindigt de oude overeenkomst; open
 *     taken escaleren gebundeld naar HR en staan op de werklijst "in gebruik
 *     zonder getekende bruikleenovereenkomst".
 *
 * Alle schema's zijn GEDEELD: de API valideert ermee (`.parse`/`.safeParse`) en
 * het formulier valideert er client-side mee vóór verzenden - één bron, dus het
 * formulier kan nooit iets sturen dat de API weigert (CLAUDE.md, Validatie).
 */
import { z } from 'zod'
import type { DocumentEntiteitType } from './documenten'

const idRef = z.string().trim().min(1).max(64)

// ─── A. Statusketen van een gegenereerde overeenkomst ────────────────────────

/**
 * De keten uit spec §3.A2: gegenereerd -> gedeeld -> wacht op handtekening ->
 * getekend. Eén afleiding, gedeeld door het vervolgblok, het medewerkerdossier
 * en (bij bruikleen) het middel, zodat dezelfde overeenkomst overal dezelfde
 * stand toont.
 */
export const OVEREENKOMST_VERVOLG_STAPPEN = [
  'GEGENEREERD',
  'GEDEELD',
  'WACHT_OP_HANDTEKENING',
  'GETEKEND',
] as const
export type OvereenkomstVervolgStap = (typeof OVEREENKOMST_VERVOLG_STAPPEN)[number]

export const OVEREENKOMST_VERVOLG_STAP_LABELS: Record<OvereenkomstVervolgStap, string> = {
  GEGENEREERD: 'Gegenereerd',
  GEDEELD: 'Gedeeld',
  WACHT_OP_HANDTEKENING: 'Wacht op handtekening',
  GETEKEND: 'Getekend',
}

/** Hoe er getekend is (of nog niet). */
export type OvereenkomstTekenRoute = 'GEEN' | 'IN_APP' | 'UPLOAD'

export const OVEREENKOMST_TEKEN_ROUTE_LABELS: Record<OvereenkomstTekenRoute, string> = {
  GEEN: 'Nog niet getekend',
  IN_APP: 'Getekend in de portal',
  UPLOAD: 'Getekend (upload)',
}

/** Eén versie van het document in het vervolgblok (nieuwste eerst). */
export interface OvereenkomstVersieDTO {
  versionNr: number
  fileName: string
  mimeType: string
  fileSize: number
  /** ISO-timestamp van de upload/generatie. */
  uploadedAt: string
  uploadedByNaam: string | null
  reden: string | null
  /** Deze versie is het buiten de app getekende exemplaar (spec §6.1). */
  getekendUpload: boolean
  /** Opgegeven tekendatum (ISO-datum) bij een getekende upload, anders null. */
  getekendOp: string | null
}

/** Eén levende koppeling van de overeenkomst (medewerker of gebruiksitem). */
export interface OvereenkomstKoppelingDTO {
  koppelingId: string
  entiteitType: DocumentEntiteitType
  entiteitId: string
  /** Leesbaar label ("Jan Jansen", "AB-123-C - Volkswagen Transporter"). */
  label: string
  /** Backoffice-pad naar het detail, of null als het niet (meer) bestaat. */
  href: string | null
  /** ISO-datum waarop de bruikleen voor deze entiteit is beëindigd (§3.6). */
  bruikleenEindDatum: string | null
  bruikleenEindReden: string | null
}

/**
 * Alles wat het gedeelde vervolgblok nodig heeft. Eén server-call na het
 * genereren (en na elke vervolgactie), zodat de knoppen nooit een gok zijn:
 * de `mag*`-vlaggen komen server-autoritatief uit dezelfde permissie-gates die
 * de mutatie-endpoints gebruiken.
 */
export interface OvereenkomstVervolgDTO {
  generatedId: string
  documentId: string
  titel: string
  templateNaam: string
  /** `DocumentTemplateCategory` als string (bruikleen, arbeidsovereenkomst, ...). */
  categorie: string
  categorieLabel: string
  /** Is dit een bruikleen-/autoregeling-overeenkomst (dan hoort er een middel bij)? */
  isBruikleen: boolean

  stap: OvereenkomstVervolgStap
  tekenRoute: OvereenkomstTekenRoute
  /** ISO-timestamp waarop er getekend is (in-app of upload), of null. */
  getekendOp: string | null
  getekendDoor: string | null

  employeeId: string | null
  employeeNaam: string | null
  /** Kan de gekoppelde medewerker digitaal ondertekenen (actief portal-account)? */
  medewerkerKanOndertekenen: boolean | null
  /** Mailadres van de medewerker (voorgevuld in het mail-veld), of null. */
  employeeEmail: string | null

  /** Vereist dit document een handtekening (FO-384-tekenflow actief)? */
  vraagtHandtekening: boolean
  /** Vrijgegeven aan de medewerker (FO-385) - ISO-timestamp of null. */
  gedeeldMetMedewerkerOp: string | null

  // ── batch-58 klant-portaal-beheer (FO-428 §3.4) ────────────────────────────
  // "Delen met klant" gebruikt EXACT hetzelfde vrijgave-mechanisme als de
  // relatie-tab en de documentenmodule (`Document.klantportaalVrijgegeven`) -
  // geen tweede checkbox met eigen semantiek.
  /** Hangt dit document aan een relatie? Zonder relatie kan er niets gedeeld. */
  klantId: string | null
  klantNaam: string | null
  /** Vrijgegeven voor het KLANTPORTAAL (D6) - ISO-timestamp of null. */
  gedeeldMetKlantOp: string | null

  koppelingen: OvereenkomstKoppelingDTO[]
  versies: OvereenkomstVersieDTO[]
  /** Laatste versienummer (download via de same-origin documenten-proxy). */
  laatsteVersieNr: number

  magKoppelen: boolean
  magDelen: boolean
  magGetekendeUploadDoen: boolean

  /**
   * De bruikleen-trigger die door deze overeenkomst is opgelost (§6.6), of null.
   * Zichtbaar als bevestiging dat de openstaande taak is afgehandeld.
   */
  opgelosteTaakTitel: string | null
}

// ─── A. Request-schema's van de vervolgacties ────────────────────────────────

/** Verwijzing naar één gebruiksitem in de koppel-actie. */
export const OvereenkomstItemRefSchema = z.object({
  type: z.enum(['VOERTUIG', 'VOORRAAD_UNIEK_ITEM']),
  id: idRef,
})
export type OvereenkomstItemRef = z.infer<typeof OvereenkomstItemRefSchema>

/**
 * Koppelen vanuit het vervolgblok. Minstens één van beide moet gevuld zijn -
 * een lege koppel-actie is geen actie en zou als stille no-op wegvallen.
 */
export const KoppelOvereenkomstRequestSchema = z
  .object({
    employeeId: idRef.nullable().optional(),
    item: OvereenkomstItemRefSchema.nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (!val.employeeId && !val.item) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['employeeId'],
        message: 'Kies een medewerker en/of een middel om aan te koppelen.',
      })
    }
  })
export type KoppelOvereenkomstRequest = z.infer<typeof KoppelOvereenkomstRequestSchema>

/**
 * Delen: zichtbaar maken voor de medewerker (FO-385) en/of mailen. Beide mogen
 * tegelijk; minstens één moet gekozen zijn.
 */
export const DeelOvereenkomstRequestSchema = z
  .object({
    /** Vrijgeven aan de medewerker (portal-inzage + vrijgavemelding). */
    vrijgevenAanMedewerker: z.boolean().optional().default(false),
    /**
     * batch-58 (FO-428 §3.4) — vrijgeven voor het KLANTPORTAAL. Zet exact
     * dezelfde D6-vrijgave als de relatie-tab en de documentenmodule; alleen
     * mogelijk als het document aan een relatie hangt.
     */
    vrijgevenAanKlant: z.boolean().optional().default(false),
    /** Mailen naar deze adressen (de PDF gaat als bijlage mee). */
    mailNaar: z
      .array(z.string().trim().email('Ongeldig e-mailadres'))
      .max(10)
      .optional()
      .default([]),
    /** Vrije begeleidende tekst in de mail (optioneel). */
    bericht: z.string().trim().max(2000).nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (
      !val.vrijgevenAanMedewerker &&
      !val.vrijgevenAanKlant &&
      (val.mailNaar?.length ?? 0) === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mailNaar'],
        message:
          'Kies vrijgeven aan de medewerker of de klant, of vul minstens één e-mailadres in.',
      })
    }
  })
export type DeelOvereenkomstRequest = z.infer<typeof DeelOvereenkomstRequestSchema>

export interface DeelOvereenkomstResultDTO {
  vrijgegeven: boolean
  /** Vrijgegeven voor het klantportaal (batch-58, FO-428). */
  vrijgegevenAanKlant: boolean
  /** Aantal adressen waar de mail daadwerkelijk heen ging (0 = niet gemaild). */
  gemaildNaar: number
  /** Reden waarom de mail niet verstuurd kon worden, of null. */
  mailFout: string | null
}

/**
 * Meta bij een getekende upload. Reist als multipart-velden mee met de
 * bestaande versie-upload (`POST /documenten/:id/versies`) - er is geen tweede
 * upload-implementatie (spec §12.1).
 */
export const GetekendeUploadMetaSchema = z.object({
  /** Markeer deze versie als "getekend (upload)". */
  getekendUpload: z.literal(true),
  /** Tekendatum als ISO-datum (jjjj-mm-dd). Leeg = vandaag. */
  getekendOp: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum in de vorm jjjj-mm-dd')
    .optional(),
})
export type GetekendeUploadMeta = z.infer<typeof GetekendeUploadMetaSchema>

/** MIME-types die als getekend exemplaar geaccepteerd worden (spec §6.1). */
export const GETEKENDE_UPLOAD_MIME_TYPES: readonly string[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
]

/** True als dit bestandstype als getekend exemplaar mag worden geüpload. */
export function isGetekendeUploadMime(mimeType: string): boolean {
  return GETEKENDE_UPLOAD_MIME_TYPES.includes(mimeType.toLowerCase())
}

// ─── B. Bruikleen-trigger ────────────────────────────────────────────────────

export const BRUIKLEEN_TRIGGER_SOORTEN = [
  'VOERTUIG_BERIJDER',
  'TELEFOON_SIM',
  'GEREEDSCHAP_SET',
  // batch-67 middelen-inname (FO-499 §B5) - eigen soorten met eigen aan/uit-instelling.
  'LAPTOP',
  'SLEUTEL',
  // batch-70 uitrusting-controle (FO-531 §D11) - ontvangst- en gebruiksverklaring kleding en PBM.
  'KLEDING_PBM',
] as const
export type BruikleenTriggerSoort = (typeof BRUIKLEEN_TRIGGER_SOORTEN)[number]

export const BRUIKLEEN_TRIGGER_SOORT_LABELS: Record<BruikleenTriggerSoort, string> = {
  VOERTUIG_BERIJDER: 'Berijderwissel',
  TELEFOON_SIM: 'Telefoon of SIM uitgegeven',
  GEREEDSCHAP_SET: 'Gereedschap uitgegeven',
  LAPTOP: 'Laptop of tablet uitgegeven',
  SLEUTEL: 'Autosleutel uitgegeven',
  KLEDING_PBM: 'Kleding en PBM verstrekt',
}

export const BRUIKLEEN_TRIGGER_STATUSSEN = ['OPEN', 'AFGEHANDELD', 'VERVALLEN'] as const
export type BruikleenTriggerStatus = (typeof BRUIKLEEN_TRIGGER_STATUSSEN)[number]

export const BRUIKLEEN_TRIGGER_STATUS_LABELS: Record<BruikleenTriggerStatus, string> = {
  OPEN: 'Open',
  AFGEHANDELD: 'Afgehandeld',
  VERVALLEN: 'Vervallen',
}

export const BRUIKLEEN_VARIANTEN = ['MET_BIJTELLING', 'ZONDER_BIJTELLING'] as const
export type BruikleenVariant = (typeof BRUIKLEEN_VARIANTEN)[number]

export const BRUIKLEEN_VARIANT_LABELS: Record<BruikleenVariant, string> = {
  MET_BIJTELLING: 'Met bijtelling',
  ZONDER_BIJTELLING: 'Zonder bijtelling',
}

/** Eén middel uit de snapshot van een uitgifte-moment. */
export interface BruikleenTriggerMiddelDTO {
  type: DocumentEntiteitType
  id: string
  label: string
}

/**
 * Zod-schema van de opgeslagen `middelen`-snapshot. De kolom is `Json`, dus de
 * lezer valideert wat hij terugkrijgt in plaats van te casten (geen `any`).
 */
export const BruikleenTriggerMiddelenSchema = z.array(
  z.object({
    type: z.string().min(1).max(64),
    id: z.string().min(1).max(64),
    label: z.string().max(300),
  }),
)

/** Eén open bruikleen-taak (werkvoorraad + offboarding-cockpit). */
export interface BruikleenTriggerDTO {
  id: string
  soort: BruikleenTriggerSoort
  soortLabel: string
  status: BruikleenTriggerStatus
  employeeId: string
  employeeNaam: string
  omschrijving: string
  middelen: BruikleenTriggerMiddelDTO[]
  variant: BruikleenVariant | null
  /** ISO-datum van het uitgifte-/wisselmoment. */
  uitgifteDatum: string
  /** Dagen dat de taak openstaat (0 = vandaag aangemaakt). */
  dagenOpen: number
  /** Staat de taak langer open dan de ingestelde drempel? */
  overDrempel: boolean
  taakId: string | null
  documentId: string | null
  /** De vorige overeenkomst is nooit getekend (spec §6.3). */
  vorigeNooitGetekend: boolean
  /** Volledig voorgevulde deeplink naar de bruikleen-wizard (§3.5). */
  wizardHref: string
  createdAt: string
}

/** Eén regel op de lijst "in gebruik zonder getekende bruikleenovereenkomst". */
export interface BruikleenOpenstaandRowDTO {
  /** Sleutel `<type>:<id>` - stabiel per middel. */
  sleutel: string
  itemType: DocumentEntiteitType
  itemId: string
  itemLabel: string
  itemHref: string
  employeeId: string
  employeeNaam: string
  /** ISO-datum sinds wanneer het middel bij deze medewerker ligt, of null. */
  sindsDatum: string | null
  /**
   * Er is wél een bruikleendocument, maar het is (nog) niet getekend. `null`
   * betekent: er is helemaal geen overeenkomst voor deze combinatie.
   */
  ongetekendDocumentId: string | null
  /** De openstaande trigger-taak voor dit middel, of null. */
  triggerId: string | null
  /** Voorgevulde wizard-link om de overeenkomst alsnog op te stellen. */
  wizardHref: string
}

export interface BruikleenOpenstaandMetaDTO {
  page: number
  pageSize: number
  /** Server-side totaal over de VOLLEDIGE gefilterde set. */
  total: number
  /**
   * De doorloop van de in-gebruik-set raakte het batch-plafond: er kan werk
   * buiten beeld zijn gebleven. Nooit stil (CLAUDE.md, Lijsten & stille afkap).
   */
  afgekapt: boolean
}

export const BruikleenOpenstaandQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional().default(1),
  pageSize: z.coerce.number().int().min(1).max(100).optional().default(25),
  /** Alleen deze medewerker (offboarding-cockpit). */
  employeeId: idRef.optional(),
  /** Zoek op middel-label of medewerkernaam. */
  zoek: z.string().trim().max(100).optional(),
})
export type BruikleenOpenstaandQuery = z.infer<typeof BruikleenOpenstaandQuerySchema>

/**
 * Grenzen van de openstaand-drempel. Eén dag is de ondergrens (alles daaronder
 * zou de escalatie in dezelfde dag laten vuren als de uitgifte); 90 dagen de
 * bovengrens (langer openstaan is geen bewaking meer).
 */
export const BRUIKLEEN_INSTELLINGEN_MIN_DAGEN = 1
export const BRUIKLEEN_INSTELLINGEN_MAX_DAGEN = 90

/** Instellingen-payload van de bewaking (spec §3.7). */
export const BruikleenInstellingenSchema = z.object({
  openstaandDrempelDagen: z
    .number()
    .int()
    .min(BRUIKLEEN_INSTELLINGEN_MIN_DAGEN)
    .max(BRUIKLEEN_INSTELLINGEN_MAX_DAGEN),
})
export type BruikleenInstellingenRequest = z.infer<typeof BruikleenInstellingenSchema>

export interface BruikleenInstellingenDTO {
  openstaandDrempelDagen: number
}

/**
 * Bouw de voorgevulde wizard-link voor een trigger (spec §3.5: "één klik naar
 * de generate-wizard, VOLLEDIG voorgevuld"). Gedeeld tussen API-DTO en UI zodat
 * beide exact dezelfde parameters gebruiken.
 */
export function bouwBruikleenWizardHref(input: {
  employeeId: string
  itemType?: string | null
  itemId?: string | null
  entityId?: string | null
  variant?: BruikleenVariant | null
  /** ISO-datum (jjjj-mm-dd) van het uitgifte-moment. */
  uitgifteDatum?: string | null
  triggerId?: string | null
}): string {
  const qs = new URLSearchParams()
  qs.set('employeeId', input.employeeId)
  if (input.itemType && input.itemId) {
    qs.set('itemType', input.itemType)
    qs.set('itemId', input.itemId)
  }
  if (input.entityId) qs.set('entityId', input.entityId)
  if (input.variant) qs.set('variant', input.variant)
  if (input.uitgifteDatum) qs.set('uitgifteDatum', input.uitgifteDatum)
  if (input.triggerId) qs.set('triggerId', input.triggerId)
  return `/bruikleen-documenten/nieuw?${qs.toString()}`
}
